import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../api";
import { Async } from "../components/Async";
import { day, hours, money, reasonText, stamp } from "../format";
import type { Candidate, Deal, Status } from "../types";

export function Overview() {
  const [onlyAccepted, setOnlyAccepted] = useState(false);
  const status = useQuery({ queryKey: ["status"], queryFn: api.status });
  const candidates = useQuery({
    queryKey: ["candidates", onlyAccepted],
    queryFn: () => api.candidates({ onlyAccepted }),
  });
  const deals = useQuery({ queryKey: ["deals"], queryFn: api.deals });

  return (
    <>
      <h1>Übersicht</h1>
      <p className="lede">
        Was der letzte Lauf gefunden hat – und bei den verworfenen Angeboten, woran es lag.
      </p>

      <Async query={status}>{(data) => <StatusCards status={data} />}</Async>

      <h2>
        Kandidaten{" "}
        <button
          type="button"
          className="quiet"
          onClick={() => setOnlyAccepted((value) => !value)}
        >
          {onlyAccepted ? "auch verworfene zeigen" : "nur regelkonforme"}
        </button>
      </h2>
      <Async
        query={candidates}
        empty="Noch keine Kandidaten. Der Worker legt sie beim nächsten Lauf an."
      >
        {(rows) => (
          <>
            {rows.map((row) => (
              <CandidateCard key={row.id} candidate={row} />
            ))}
          </>
        )}
      </Async>

      <h2>Extern gemeldet</h2>
      <p className="muted" style={{ marginTop: -4 }}>
        Treffer aus Deal-Feeds und Reddit. Eine Meldung ist ein Hinweis, kein Angebot – sie
        enthält keine Flugdaten und lässt sich nicht automatisch prüfen.
      </p>
      <Async
        query={deals}
        empty="Noch keine passenden Meldungen. Der Aggregator läuft mit dem nächsten Worker-Lauf."
      >
        {(rows) => (
          <>
            {rows.map((deal) => (
              <DealCard key={deal.id} deal={deal} />
            ))}
          </>
        )}
      </Async>
    </>
  );
}

function DealCard({ deal }: { deal: Deal }) {
  return (
    <article className="candidate unverified">
      <div className="row">
        <a href={deal.url} target="_blank" rel="noreferrer noopener">
          {deal.title}
        </a>
        <span className="badge warn">noch zu verifizieren</span>
      </div>
      <div className="meta">
        {deal.source}
        {deal.published_at ? ` · ${day(deal.published_at)}` : ""} · Stichwörter:{" "}
        {deal.matched_keywords.join(", ")}
      </div>
      {deal.summary && <div className="meta">{deal.summary.slice(0, 220)}</div>}
    </article>
  );
}

function CheckoutEvidenceRow({ candidate }: { candidate: Candidate }) {
  const check = candidate.checkout;
  if (!check) return null;

  const verified = check.final_price_cents;
  // Weicht der nachgeprüfte Preis ab, ist das die eigentliche Nachricht --
  // der Sweep liest zwischengespeicherte Werte, die Prüfung die Seite selbst.
  const drift = verified !== null ? verified - candidate.price_cents : null;

  return (
    <div className="meta">
      Nachgeprüft über {check.domain}
      {verified !== null ? `: ${money(verified)}` : ""}
      {drift !== null && drift !== 0
        ? ` (${drift > 0 ? "+" : ""}${money(drift)} gegenüber dem Fund)`
        : ""}
      {" · bis "}
      {check.reached_stage}
      {check.has_screenshot ? (
        <>
          {" · "}
          <a
            href={`/api/candidates/${candidate.id}/screenshot`}
            target="_blank"
            rel="noreferrer noopener"
          >
            Beleg ansehen
          </a>
        </>
      ) : null}
      {check.error ? ` · ${check.error}` : ""}
    </div>
  );
}

function StatusCards({ status }: { status: Status }) {
  const run = status.last_run;
  return (
    <>
      <div className="cards">
        <div className="card">
          <div className="label">Beobachtungen</div>
          <div className="value">{status.observations.toLocaleString("de-DE")}</div>
        </div>
        <div className="card">
          <div className="label">Kandidaten</div>
          <div className="value">{status.candidates}</div>
        </div>
        <div className="card">
          <div className="label">davon regelkonform</div>
          <div className="value">{status.accepted_candidates}</div>
        </div>
        <div className="card">
          <div className="label">Extern gemeldet</div>
          <div className="value">{status.external_deals}</div>
        </div>
        <div className="card">
          <div className="label">Letzter Lauf</div>
          <div className="value" style={{ fontSize: 17 }}>
            {run ? run.status : "–"}
          </div>
          <div className="muted">{run ? stamp(run.started_at) : "noch keiner"}</div>
        </div>
      </div>

      {!status.scraping_enabled && (
        <div className="notice">
          Der Scraping-Pfad ist abgeschaltet. Er trägt die Grobsuche – ohne ihn liefert
          Travelpayouts nur rund 20 Beobachtungen je Lauf. Einschalten über
          <code> PRICECHECKER_ENABLE_SCRAPING_PROVIDERS=true</code>.
        </div>
      )}

      <h2>Kontingent</h2>
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Quelle</th>
              <th className="num">verbraucht</th>
              <th className="num">Grenze</th>
              <th className="num">frei</th>
            </tr>
          </thead>
          <tbody>
            {status.quota.map((entry) => (
              <tr key={entry.provider}>
                <td>{entry.provider}</td>
                <td className="num">{entry.used.toLocaleString("de-DE")}</td>
                <td className="num">{entry.limit.toLocaleString("de-DE")}</td>
                <td className="num">{entry.remaining.toLocaleString("de-DE")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function CandidateCard({ candidate }: { candidate: Candidate }) {
  const notable = candidate.verdicts.filter((verdict) => verdict.outcome !== "pass");
  return (
    <article className={`candidate ${candidate.accepted ? "accepted" : "rejected"}`}>
      <div className="row">
        <span className="price" title="Gesamtpreis für Hin- und Rückflug, ein Erwachsener, Economy">
          {money(candidate.price_cents)}
        </span>
        <span className="meta">Hin und zurück, 1 Person</span>
        <span>
          {candidate.origin} → {candidate.destination}
        </span>
        <span className="meta">
          {day(candidate.depart_date)}
          {candidate.return_date ? ` – ${day(candidate.return_date)}` : ""}
          {candidate.trip_days !== null ? ` (${candidate.trip_days} Tage)` : ""}
        </span>
        <span className={`badge ${candidate.accepted ? "ok" : "bad"}`}>
          {candidate.accepted ? "regelkonform" : "verworfen"}
        </span>
      </div>

      <div className="meta">
        Hinflug {hours(candidate.outbound_minutes)}
        {candidate.inbound_minutes !== null
          ? `, Rückflug ${hours(candidate.inbound_minutes)}`
          : ", Rückflugdauer nicht abgerufen"}
        {candidate.carrier ? ` · ${candidate.carrier}` : ""} · {candidate.source}
        {candidate.deep_link ? (
          <>
            {" · "}
            <a href={candidate.deep_link} target="_blank" rel="noreferrer noopener">
              ansehen
            </a>
          </>
        ) : null}
      </div>

      {candidate.stopover && (
        <div className="meta">
          Stopover {candidate.stopover.hub_name} ({candidate.stopover.hub}),{" "}
          {Math.round(candidate.stopover.layover_minutes / 60)} h,{" "}
          {candidate.stopover.direction === "outbound" ? "Hinweg" : "Rückweg"}
          {candidate.stopover.official_program
            ? " · offizielles Stopover-Programm der Airline"
            : ""}
        </div>
      )}

      {candidate.checkout && <CheckoutEvidenceRow candidate={candidate} />}

      {notable.length > 0 && (
        <ul className="reasons">
          {notable.map((verdict, index) => (
            <li key={index} className={verdict.outcome}>
              {reasonText(verdict.reason)}
              {verdict.direction ? (verdict.direction === "outbound" ? " (Hinweg)" : " (Rückweg)") : ""}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
